import { createSelector } from "@reduxjs/toolkit";

import { selectMultiImages } from "../../../../components/ImageUploader/images.slice";
import { FormId } from "../../../../components/enums";
import { RootState } from "../../../../store";
import { UIState } from "../../../../store/HOC/genericUI.slice";

import { ComponentId as AdditionalComponentId } from "./AdditionalData/enums";
import { ComponentId as ImagesDescriptionComponentId } from "./ImagesDescriptionData/enums";
import {
  DamagesAdditionalFormInputs,
  DamagesImagesDescriptionFormInputs,
  FormDataInputs,
} from "./damages.slice";

export const selectDamagesImagesDescriptionData = (
  store: RootState
): DamagesImagesDescriptionFormInputs =>
  store.ocVehicle.damages.data[FormId.DAMAGES_IMAGES_DESCRIPTION];

export const selectDamagesAdditionalData = (
  store: RootState
): DamagesAdditionalFormInputs =>
  store.ocVehicle.damages.data[FormId.DAMAGES_ADDITIONAL];

export const selectArePreviousDamagePresent = createSelector(
  selectDamagesAdditionalData,
  (additionalData) =>
    additionalData[AdditionalComponentId.ARE_PREVIOUS_DAMAGE_PRESENT] === "true"
);

export const selectIsVehicleNotWorking = createSelector(
  selectDamagesAdditionalData,
  (additionalData) =>
    additionalData[AdditionalComponentId.IS_VEHICLE_WORKING] === "false"
);

export const selectIsReplacementVehicleRequired = createSelector(
  selectDamagesAdditionalData,
  (additionalData) =>
    additionalData[AdditionalComponentId.IS_REPLACEMENT_VEHICLE_REQUIRED] ===
    "true"
);

export const selectAreOtherDamagesPresent = createSelector(
  selectDamagesAdditionalData,
  (additionalData) =>
    additionalData[AdditionalComponentId.ARE_OTHER_DAMAGES_PRESENT] === "true"
);

const selectDamagesDescription = createSelector(
  selectDamagesImagesDescriptionData,
  (data) => ({
    name: "Opis uszkodzeń",
    value: data[ImagesDescriptionComponentId.DESCRIPTION],
  })
);

const selectDamagesImages = createSelector(
  (data: RootState) =>
    selectMultiImages(data, {
      section: ImagesDescriptionComponentId.DAMAGE_IMAGES,
    }),
  (data) => ({
    name: "Zdjęcia pojazdu",
    value: Object.values(data).map((image) => image.src as string),
  })
);

const selectPreviousDamage = createSelector(
  selectDamagesAdditionalData,
  (data) => ({
    name:
      "Czy pojazd przed zdarzeniem posiadał jakieś inne widoczne uszkodzenia?",
    value:
      data[AdditionalComponentId.ARE_PREVIOUS_DAMAGE_PRESENT] === "true"
        ? "tak"
        : "nie",
  })
);

const selectPreviousDamageDescription = createSelector(
  selectDamagesAdditionalData,
  (data) => ({
    name: "Opis wcześniejszych uszkodzeń",
    value: data[AdditionalComponentId.PREVIOUS_DAMAGE_DESCRIPTION],
  })
);

const selectAirbagsDeployed = createSelector(
  selectDamagesAdditionalData,
  (data) => ({
    name: "Czy zadziałały poduszki powietrzne",
    value:
      data[AdditionalComponentId.AIRBAGS_DEPLOYED] === "true" ? "tak" : "nie",
  })
);

const selectVehicleWorking = createSelector(
  selectDamagesAdditionalData,
  (data) => ({
    name: "Czy pojazd jest jezdny i można się nim bezpiecznie poruszać?",
    value:
      data[AdditionalComponentId.IS_VEHICLE_WORKING] === "true" ? "tak" : "nie",
  })
);

const selectReplacementVehicle = createSelector(
  selectDamagesAdditionalData,
  (data) => ({
    name: "Czy wymagany jest pojazd zastępczy?",
    value:
      data[AdditionalComponentId.IS_REPLACEMENT_VEHICLE_REQUIRED] === "true"
        ? "tak"
        : "nie",
  })
);

const selectOtherDamage = createSelector(
  selectDamagesAdditionalData,
  (data) => ({
    name: "Czy powstały inne szkody rzeczowe poza uszkodzonym pojazdem?",
    value:
      data[AdditionalComponentId.ARE_OTHER_DAMAGES_PRESENT] === "true"
        ? "tak"
        : "nie",
  })
);

const selectOtherDamageDescription = createSelector(
  selectDamagesAdditionalData,
  (data) => ({
    name: "Opis dodatkowych szkód",
    value: data[AdditionalComponentId.OTHER_DAMAGES_DESCRIPTION],
  })
);

const selectDamagesData = createSelector(
  selectDamagesDescription,
  selectDamagesImages,
  selectPreviousDamage,
  selectPreviousDamageDescription,
  selectAirbagsDeployed,
  selectVehicleWorking,
  selectReplacementVehicle,
  selectOtherDamage,
  selectOtherDamageDescription,
  (
    damagesDescription,
    damagesImages,
    previousDamage,
    previousDamageDescription,
    airbags,
    vehicleWorking,
    replacementVehicle,
    otherDamage,
    otherDamageDescription
  ) => [
    damagesDescription,
    damagesImages,
    previousDamage,
    previousDamageDescription,
    airbags,
    vehicleWorking,
    replacementVehicle,
    otherDamage,
    otherDamageDescription,
  ]
);

export const selectSummaryData = createSelector(
  selectDamagesData,
  (damages) => ({ damages })
);

export const selectFormData = (store: RootState): FormDataInputs =>
  store.ocVehicle.damages.data;

export const selectUIData = (store: RootState): UIState =>
  store.ocVehicle.damages.ui;
